import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Bell, BellOff, CalendarDays, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reminders() {
  const { user, getToken } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [shows, setShows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ show_id: '', title: '', reminder_date: '', note: '' });
  const [selectedDate, setSelectedDate] = useState(null);
  const [tipDismissed, setTipDismissed] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [remRes, showsRes] = await Promise.all([
        axios.get(`${API}/reminders`, { headers }),
        axios.get(`${API}/shows`, { headers }),
      ]);
      setReminders(remRes.data);
      setShows(showsRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [getToken]);

  useEffect(() => { if (user) fetchData(); }, [fetchData, user]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setForm({ ...form, reminder_date: format(date, 'yyyy-MM-dd') });
  };

  const addReminder = async () => {
    if (!form.title || !form.reminder_date) {
      toast.error('Title and date are required');
      return;
    }
    try {
      const token = await getToken();
      await axios.post(`${API}/reminders`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Reminder set!');
      setForm({ show_id: '', title: '', reminder_date: '', note: '' });
      setSelectedDate(null);
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create reminder');
    }
  };

  const toggleReminder = async (reminder) => {
    try {
      const token = await getToken();
      await axios.put(`${API}/reminders/${reminder.reminder_id}`, { is_active: !reminder.is_active }, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      toast.error('Failed to update reminder');
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      const token = await getToken();
      await axios.delete(`${API}/reminders/${reminderId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Reminder deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete reminder');
    }
  };

  const activeReminders = reminders.filter(r => r.is_active);
  const inactiveReminders = reminders.filter(r => !r.is_active);

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="reminders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>
            🔔 Reminders &amp; Alerts
          </h1>
          <p className="text-neutral-500 mt-1">Never miss a premiere or new episode</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold gap-2 hover:opacity-90" data-testid="add-reminder-btn">
              <Plus className="w-4 h-4" /> New Reminder
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-bold" style={{ fontFamily: 'Outfit' }}>Set a Reminder</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                placeholder="Reminder title (e.g., Season 2 premiere)"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12"
                data-testid="reminder-title-input"
              />
              {shows.length > 0 && (
                <Select value={form.show_id || 'none'} onValueChange={v => setForm({ ...form, show_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-white rounded-xl h-12" data-testid="reminder-show-select">
                    <SelectValue placeholder="Link to a show (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 text-white">
                    <SelectItem value="none">No show linked</SelectItem>
                    {shows.map(s => (
                      <SelectItem key={s.show_id} value={s.show_id}>{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-black/20 border-white/10 text-white rounded-xl h-12 hover:bg-white/5" data-testid="reminder-date-picker">
                    <CalendarDays className="w-4 h-4 mr-2 text-neutral-400" />
                    {selectedDate ? format(selectedDate, 'PPP') : <span className="text-neutral-600">Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#141414] border-white/10" align="start">
                  <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} className="rounded-xl" />
                </PopoverContent>
              </Popover>
              <Input
                placeholder="Note (optional)"
                value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
                className="bg-black/20 border-white/10 text-white placeholder:text-neutral-600 rounded-xl h-12"
                data-testid="reminder-note-input"
              />
              <Button onClick={addReminder} className="w-full bg-gradient-to-r from-violet-600 to-[#4A9EFF] text-white rounded-full font-semibold hover:opacity-90" data-testid="submit-reminder-btn">
                Set Reminder
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tip banner — shown only when no reminders exist and not dismissed */}
      {reminders.length === 0 && !tipDismissed && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-2xl border border-violet-500/20 bg-violet-500/5">
          <span className="text-lg flex-shrink-0">💡</span>
          <p className="text-sm text-neutral-300 flex-1">
            <span className="font-semibold text-white">Tip:</span> Set a reminder from any Hub card using the <span className="font-mono text-violet-300">⋯</span> menu
          </p>
          <button
            onClick={() => setTipDismissed(true)}
            className="text-neutral-600 hover:text-neutral-400 flex-shrink-0 mt-0.5"
            aria-label="Dismiss tip"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div>
        <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4">Active ({activeReminders.length})</h2>
        {activeReminders.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
            <p className="text-neutral-500">No active reminders. Set one to never miss a show!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeReminders.map(rem => {
              const show = shows.find(s => s.show_id === rem.show_id);
              return (
                <div key={rem.reminder_id} className="flex items-center gap-4 p-4 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-white/10 transition-all" data-testid={`reminder-${rem.reminder_id}`}>
                  <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                    <Bell className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{rem.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs font-mono text-neutral-500">{rem.reminder_date}</span>
                      {show && <span className="text-xs text-neutral-600">- {show.title}</span>}
                    </div>
                    {rem.note && <p className="text-xs text-neutral-600 mt-1">{rem.note}</p>}
                  </div>
                  <button onClick={() => toggleReminder(rem)} className="p-2 text-neutral-500 hover:text-amber-400 transition-colors" data-testid={`toggle-reminder-${rem.reminder_id}`}>
                    <BellOff className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteReminder(rem.reminder_id)} className="p-2 text-neutral-600 hover:text-red-400 transition-colors" data-testid={`delete-reminder-${rem.reminder_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {inactiveReminders.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4">Dismissed ({inactiveReminders.length})</h2>
          <div className="space-y-3 opacity-60">
            {inactiveReminders.map(rem => (
              <div key={rem.reminder_id} className="flex items-center gap-4 p-4 rounded-xl bg-[#0A0A0A] border border-white/5" data-testid={`inactive-reminder-${rem.reminder_id}`}>
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                  <BellOff className="w-5 h-5 text-neutral-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-400">{rem.title}</p>
                  <span className="text-xs font-mono text-neutral-600">{rem.reminder_date}</span>
                </div>
                <button onClick={() => toggleReminder(rem)} className="p-2 text-neutral-500 hover:text-emerald-400 transition-colors" data-testid={`reactivate-reminder-${rem.reminder_id}`}>
                  <Bell className="w-4 h-4" />
                </button>
                <button onClick={() => deleteReminder(rem.reminder_id)} className="p-2 text-neutral-600 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
